import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '../services/authService';
import { setUnauthorizedHandler } from '../services/core/authEvents';
import type { User } from '../types/auth';

interface AuthStoreState {
  token: string | null;
  user: User | null;
  isLoggedIn: boolean;
  isLoginModalOpen: boolean;
  /** 会话过期标记：弹登录框时展示「登录已过期」提示，登录成功/手动关闭后复位 */
  sessionExpired: boolean;
  pendingAction: (() => void) | null;
  login: (phone: string, code: string, inviteCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  openLoginModal: (pendingAction?: () => void) => void;
  closeLoginModal: () => void;
  fetchUser: () => Promise<void>;
  setUser: (user: User | null) => void;
  /** 积分余额刷新（/api/user/points 分池口径，局部合并 user.points / user.dailyPoints） */
  refreshPoints: () => Promise<void>;
  setToken: (token: string | null) => void;
  runPendingAction: () => void;
  /** 登录态失效（任意接口返回 401）时的统一清理入口 */
  expireSession: () => void;
}

export const useAuthStore = create<AuthStoreState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isLoggedIn: false,
      isLoginModalOpen: false,
      sessionExpired: false,
      pendingAction: null,

      login: async (phone: string, code: string, inviteCode?: string) => {
        const { token, user } = await authService.login(phone, code, inviteCode);
        localStorage.setItem('bongai_token', token);
        set({ token, user, isLoggedIn: true, isLoginModalOpen: false, sessionExpired: false });
        get().runPendingAction();
      },

      logout: async () => {
        // 先清理本地登录态，保证后端不可用时登出仍然即时响应
        localStorage.removeItem('bongai_token');
        set({ token: null, user: null, isLoggedIn: false });
        // 同步清空项目数据，避免项目管理页残留上一账号内容
        try {
          const m = await import('./projectStore');
          m.useProjectStore.getState().clearProjects();
        } catch {}
        // 后端登出调用带 3s 超时（BaseService 默认无超时，后端挂死时会永久阻塞）
        try {
          await Promise.race([
            authService.logout(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('logout timeout')), 3000)),
          ]);
        } catch {}
      },

      openLoginModal: (pendingAction) => {
        set({ isLoginModalOpen: true, pendingAction: pendingAction || null });
      },

      closeLoginModal: () => {
        set({ isLoginModalOpen: false, pendingAction: null, sessionExpired: false });
      },

      fetchUser: async () => {
        const token = get().token;
        if (!token) return;
        // 并行发起：登录态校验与项目列表拉取互不等待。
        // token 失效时项目接口返回 401，由全局拦截器统一触发 expireSession，因此可以安全并行
        void import('./projectStore').then(m => m.useProjectStore.getState().loadProjects());
        try {
          const user = await authService.getMe();
          set({ user, isLoggedIn: true });
        } catch (e: any) {
          // 仅认证类错误（401/403）清除登录态（getMe 的 401 已由全局拦截器触发 expireSession，此处幂等兜底）
          // 网络抖动/后端临时不可用保留本地登录态，避免「断网被误登出」
          const isAuthError = e?.name === 'ApiError' && (e?.status === 401 || e?.status === 403);
          if (isAuthError) {
            localStorage.removeItem('bongai_token');
            set({ token: null, user: null, isLoggedIn: false });
          } else {
            console.warn('[Auth] getMe 校验失败（非认证错误），保留本地登录态:', e?.message);
          }
        }
      },

      expireSession: () => {
        // 主动登出（logout 先置 isLoggedIn=false 再调后端）或已过期时不重复弹窗
        if (!get().isLoggedIn) return;
        localStorage.removeItem('bongai_token');
        set({
          token: null,
          user: null,
          isLoggedIn: false,
          isLoginModalOpen: true,
          sessionExpired: true,
          pendingAction: null,
        });
        // 同步清空项目数据，避免残留上一账号/过期会话内容
        import('./projectStore')
          .then(m => m.useProjectStore.getState().clearProjects())
          .catch(() => {});
      },

      setUser: (user) => set({ user }),

      refreshPoints: async () => {
        // 仅局部合并积分分池（不整体覆盖 user，避免并发中的 me 周期字段被旧数据冲掉）；
        // persist 中间件自动落 localStorage
        if (!get().isLoggedIn) return;
        try {
          const { points, dailyPoints } = await authService.getPoints();
          const user = get().user;
          if (!user || (user.points === points && (user.dailyPoints ?? 0) === dailyPoints)) return;
          set({ user: { ...user, points, dailyPoints } });
        } catch {
          // 静默容错：余额展示非关键路径，下次操作响应或 fetchUser 时再同步
        }
      },

      setToken: (token) => set({ token, isLoggedIn: !!token }),

      runPendingAction: () => {
        const { pendingAction } = get();
        if (pendingAction) {
          pendingAction();
          set({ pendingAction: null });
        }
      },
    }),
    {
      name: 'bongai-auth',
      partialize: (state) => ({ token: state.token, user: state.user, isLoggedIn: state.isLoggedIn }),
    }
  )
);

// 注册全局 401 处理：任意接口返回 401 时统一清登录态 + 弹「登录已过期」登录框
setUnauthorizedHandler(() => useAuthStore.getState().expireSession());
